import { restConfig } from "../authConfig";
import { AddRepresentativeModel, AmlRiskClass, CompanyModel, CompanyMonitorDetails } from "../redux/companyMonitorTypes";
import { useAppDispatch } from "../redux/hooks";
import { 
  addRepresentativeFailure, 
  amlRiskClassificationSet, 
  amlRiskClassificationSetFailure, 
  companiesWithUnhandledChangesFetchFailure, 
  companiesWithUnhandledChangesFetched, 
  companyMonitorDetailsFetchFailure, 
  companyMonitorDetailsFetched, 
  companyReportChangeCommentFailure, 
  companyReportChangeHandleFailure, 
  deleteRepresentativeFailure, 
  loading, 
  reportsRegenerated, 
  reportsRegenerationFailure } from "../redux/slices/companyMonitorSlice";
import { validateCompanyRepresentative } from "../utils/validation";
import useFetchClient from "./useFetchClient";

const useCompanyMonitorActions = () => {
    const fetchClient = useFetchClient()
    const dispatch = useAppDispatch()

    // `background` is for refreshes triggered as a side effect of another action: they
    // must not drive the shared loading indicator, and a failure must not surface as an
    // error for the action that just succeeded.
    const getCompaniesWithUnhandledChanges = (background: boolean = false) => {
      if (!background) {
        dispatch(loading())
      }

      const url = restConfig.routes.companyMonitor + `/withUnhandledChanges`

      return fetchClient.get(url)
        .then(response => {
            dispatch(companiesWithUnhandledChangesFetched(response.data as CompanyModel[]))
        })
        .catch(error => {
            console.log('getCompaniesWithUnhandledChanges error:', error)
            if (!background) {
              dispatch(companiesWithUnhandledChangesFetchFailure(error))
            }
        })
    }

    const getDetailsForCompany = (marketId: string, companyRegistrationNumber: string, reportId?: string ) => {
      dispatch(loading())
      let url = restConfig.routes.companyMonitor + `/${marketId}/${companyRegistrationNumber}`

      if (reportId)
      {
          url += `?reportId=${reportId}`
      }
  
      return fetchClient.get(url)
        .then(response => {
            dispatch(companyMonitorDetailsFetched(response.data as CompanyMonitorDetails))
        })
        .catch(error => {
            console.log('getDetailsForCompany error:', error)
            dispatch(companyMonitorDetailsFetchFailure(error))
        })
    }

    const addRepresentative = (companyId: string, representative: AddRepresentativeModel) => {
      dispatch(loading())
      const url = restConfig.routes.companyMonitor + `/${companyId}/representatives`
  
      const validationErrors = validateCompanyRepresentative(representative)
      if (validationErrors) {
        dispatch(addRepresentativeFailure(validationErrors[0]))
        return
      }
  
      return fetchClient.post(url, representative)
        .then(response => {
          dispatch(companyMonitorDetailsFetched(response.data as CompanyMonitorDetails))
        })
        .catch(error => {
            console.log('addRepresentative error:', error)
            dispatch(addRepresentativeFailure(error))
        })
      }
    
      const deleteRepresentative = (companyId: string, representativeId: string) => {
        dispatch(loading())
        const url = restConfig.routes.companyMonitor + `/${companyId}/representatives/${representativeId}`
    
        return fetchClient.delete(url)
          .then(response => {
            dispatch(companyMonitorDetailsFetched(response.data as CompanyMonitorDetails))
          })
          .catch(error => {
              console.log('deleteRepresentative error:', error)
              dispatch(deleteRepresentativeFailure(error))
          })
      }

      const handleCompanyReportChange = (companyId: string, companyReportChangeId: string, comment: string) => {
        dispatch(loading())
        const url = restConfig.routes.companyMonitor + `/${companyId}/reportChanges/${companyReportChangeId}`
        return fetchClient.post(url, { comment: comment })
          .then(response => {
            dispatch(companyMonitorDetailsFetched(response.data as CompanyMonitorDetails))
            getCompaniesWithUnhandledChanges(true)
          })
          .catch(error => {
            console.log('handleCompanyReportChange error:', error)
            dispatch(companyReportChangeHandleFailure(error))
          })
      }

      const commentCompanyReportChange = (companyId: string, companyReportChangeId: string, comment: string) => {
        dispatch(loading())
        const url = restConfig.routes.companyMonitor + `/${companyId}/reportChanges/${companyReportChangeId}/comment`
        return fetchClient.post(url, { comment: comment })
          .then(response => {
            dispatch(companyMonitorDetailsFetched(response.data as CompanyMonitorDetails))
          })
          .catch(error => {
            console.log('commentCompanyReportChange error:', error)
            dispatch(companyReportChangeCommentFailure(error))
          })
      }

      const setAmlRiskClassification = (companyId: string, riskClass: AmlRiskClass) => {
        dispatch(loading())   
        const url = restConfig.routes.companyMonitor + `/${companyId}/amlriskclassification`

        return fetchClient.post(url, { riskClass: riskClass })
          .then(response => {
              dispatch(amlRiskClassificationSet(response.data as CompanyMonitorDetails))
          })
          .catch(error => {
              console.log('setAmlRiskClassification error:', error)
              dispatch(amlRiskClassificationSetFailure(error))
        })    
      }

      const regenerateReports = (companyId: string) => {
        dispatch(loading())
        const url = restConfig.routes.companyMonitor + `/${companyId}/reports/regenerate`

        return fetchClient.post(url)
          .then(response => {
              dispatch(reportsRegenerated(response.data as CompanyMonitorDetails))
              getCompaniesWithUnhandledChanges(true)
              return true
          })
          .catch(error => {
              console.log('regenerateReports error:', error)
              dispatch(reportsRegenerationFailure(error))
              return false
        })
      }

      return {
        getCompaniesWithUnhandledChanges,
        getDetailsForCompany,
        addRepresentative,
        deleteRepresentative,
        handleCompanyReportChange,
        commentCompanyReportChange,
        setAmlRiskClassification,
        regenerateReports
      }
 }

export default useCompanyMonitorActions